import { fetchSessionUser } from "./sessionUser";

const STORAGE_KEY = "apitomcp.auth-resume";

export type AuthResumeIntent =
  | { kind: "upload-file"; fileName: string; fileType: string; dataBase64: string }
  | { kind: "fetch-url"; url: string }
  | { kind: "paste-preview"; text: string }
  | {
      kind: "convert-host";
      source: "paste" | "url" | "file";
      url?: string;
      text?: string;
      fileName?: string;
      fileType?: string;
      dataBase64?: string;
      /** Preserved across sign-in redirect so hosted MCP gets credentials. */
      credentials?: Record<string, string>;
      baseUrlOverride?: string;
    };

export type ConvertHostIntent = Extract<AuthResumeIntent, { kind: "convert-host" }>;

export type AuthResumeHandlers = {
  uploadFile: (file: File) => Promise<void>;
  fetchUrl: (url: string) => Promise<void>;
  pastePreview: (text: string) => Promise<void>;
  convertHost: (intent?: ConvertHostIntent) => Promise<void>;
};

let pendingAction: (() => void | Promise<void>) | null = null;

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function base64ToFile(fileName: string, fileType: string, dataBase64: string): File {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: fileType || "application/octet-stream" });
}

export async function fileToUploadIntent(file: File): Promise<AuthResumeIntent> {
  return {
    kind: "upload-file",
    fileName: file.name,
    fileType: file.type,
    dataBase64: await readFileAsBase64(file),
  };
}

export function capturePendingAuthAction(
  action: () => void | Promise<void>,
  intent: AuthResumeIntent,
): void {
  pendingAction = action;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
}

export function clearPendingAuthAction(): void {
  pendingAction = null;
  sessionStorage.removeItem(STORAGE_KEY);
}

export function readPendingAuthIntent(): AuthResumeIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthResumeIntent;
  } catch {
    return null;
  }
}

async function runIntent(intent: AuthResumeIntent, handlers: AuthResumeHandlers): Promise<void> {
  switch (intent.kind) {
    case "upload-file":
      await handlers.uploadFile(
        base64ToFile(intent.fileName, intent.fileType, intent.dataBase64),
      );
      return;
    case "fetch-url":
      await handlers.fetchUrl(intent.url);
      return;
    case "paste-preview":
      await handlers.pastePreview(intent.text);
      return;
    case "convert-host":
      await handlers.convertHost(intent);
      return;
    default:
      return;
  }
}

export async function tryResumeAuthWorkflow(handlers: AuthResumeHandlers): Promise<boolean> {
  const user = await fetchSessionUser();
  if (!user?.email) return false;

  if (pendingAction) {
    const action = pendingAction;
    pendingAction = null;
    sessionStorage.removeItem(STORAGE_KEY);
    await action();
    return true;
  }

  const intent = readPendingAuthIntent();
  if (!intent) return false;

  clearPendingAuthAction();
  await runIntent(intent, handlers);
  return true;
}
